/**
 * Attribution — captures traffic source data from the URL and browser.
 *
 * Parses UTM params, ad platform click IDs, referrer, device, and
 * derives a normalized channel. Called once on page load before the
 * handshake, and sent to the backend as conversation metadata.
 *
 * The source is remembered for the visit (last non-direct touch, 30 days):
 * without it, a visitor who came from an ad and browsed two pages before
 * opening the chat was reported as a referral from the site itself.
 */

import {
  readStoredAttribution,
  writeStoredAttribution,
} from '../client/storage';

export interface Attribution {
  // Level 1 — raw (captured from URL/browser)
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  click_id?: string;
  click_id_type?:
    | 'fbclid'
    | 'gclid'
    | 'ttclid'
    | 'msclkid'
    | 'li_fat_id'
    | 'twclid';
  referrer?: string;
  landing_slug?: string;
  landed_at: string;
  device: 'mobile' | 'desktop';

  // Level 2 — derived
  channel: string;
  is_paid: boolean;

  /** The referrer is the host site itself (the visitor navigated inside it). */
  referrer_internal?: boolean;
  /**
   * The source came from an earlier page of the visit (stored touch), not from
   * the page where the chat was opened. `landed_at` / `landing_slug` are then
   * the ones of that earlier page.
   */
  inherited?: boolean;
}

const CLICK_ID_PARAMS: Record<string, Attribution['click_id_type']> = {
  fbclid: 'fbclid', // Meta (Facebook, Instagram)
  gclid: 'gclid', // Google Ads
  ttclid: 'ttclid', // TikTok Ads
  msclkid: 'msclkid', // Microsoft Ads (Bing)
  li_fat_id: 'li_fat_id', // LinkedIn Ads
  twclid: 'twclid', // Twitter/X Ads
};

function detectDevice(userAgent?: string): 'mobile' | 'desktop' {
  if (!userAgent) return 'desktop';
  return /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)
    ? 'mobile'
    : 'desktop';
}

function deriveChannel(
  clickIdType?: string,
  utmSource?: string,
  referrer?: string,
  referrerInternal?: boolean,
): string {
  // 1. Click ID → paid ad platform
  if (clickIdType === 'fbclid') return 'meta_ads';
  if (clickIdType === 'gclid') return 'google_ads';
  if (clickIdType === 'ttclid') return 'tiktok_ads';
  if (clickIdType === 'msclkid') return 'microsoft_ads';
  if (clickIdType === 'li_fat_id') return 'linkedin_ads';
  if (clickIdType === 'twclid') return 'twitter_ads';

  // 2. UTM source
  if (utmSource) {
    const src = utmSource.toLowerCase();
    if (
      src.includes('facebook') ||
      src.includes('instagram') ||
      src.includes('meta')
    )
      return 'meta';
    if (src.includes('google')) return 'google';
    if (src.includes('tiktok')) return 'tiktok';
    if (src.includes('email') || src.includes('correo')) return 'email';
    if (src.includes('qr')) return 'qr';
    if (src.includes('whatsapp')) return 'whatsapp';
    return src;
  }

  // 3. Referrer (navigation inside the host site says nothing about the source)
  if (referrer && !referrerInternal) {
    try {
      const host = new URL(referrer).hostname;
      if (host.includes('facebook.com') || host.includes('instagram.com'))
        return 'meta_organic';
      if (host.includes('google.')) return 'google_organic';
      if (host.includes('tiktok.com')) return 'tiktok_organic';
      if (host.includes('twitter.com') || host.includes('x.com'))
        return 'twitter_organic';
      if (host.includes('linkedin.com')) return 'linkedin_organic';
      return 'referral';
    } catch {
      return 'referral';
    }
  }

  // 4. Nothing → direct
  return 'direct';
}

/** Hostname without a leading `www.`, so `www.site.com` and `site.com` match. */
function bareHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, '');
}

function isInternalReferrer(referrer: string, pageHost: string): boolean {
  try {
    return bareHost(new URL(referrer).hostname) === bareHost(pageHost);
  } catch {
    return false;
  }
}

/** What the current page knows, decoupled from `window` so it can be tested. */
export interface PageSignals {
  search: string;
  pathname: string;
  host: string;
  referrer?: string;
  userAgent?: string;
  now: Date;
}

/** Attribution of a single page load: URL params, referrer and device. */
export function readTouch(page: PageSignals): Attribution {
  const params = new URLSearchParams(page.search);

  // Extract UTMs
  const utm_source = params.get('utm_source') || undefined;
  const utm_medium = params.get('utm_medium') || undefined;
  const utm_campaign = params.get('utm_campaign') || undefined;
  const utm_content = params.get('utm_content') || undefined;
  const utm_term = params.get('utm_term') || undefined;

  // Extract click IDs (first match wins)
  let click_id: string | undefined;
  let click_id_type: Attribution['click_id_type'] | undefined;

  for (const [param, type] of Object.entries(CLICK_ID_PARAMS)) {
    const val = params.get(param);
    if (val) {
      click_id = val;
      click_id_type = type;
      break;
    }
  }

  // Referrer (empty string if direct or blocked by the referrer policy)
  const referrer = page.referrer || undefined;
  const referrer_internal = referrer
    ? isInternalReferrer(referrer, page.host)
    : false;

  // Landing slug from pathname
  const pathParts = page.pathname.split('/').filter(Boolean);
  const landing_slug = pathParts[pathParts.length - 1] || undefined;

  // Derive
  const channel = deriveChannel(
    click_id_type,
    utm_source,
    referrer,
    referrer_internal,
  );
  const is_paid =
    !!click_id ||
    utm_medium === 'cpc' ||
    utm_medium === 'paid' ||
    utm_medium === 'ppc';

  return {
    ...(utm_source && { utm_source }),
    ...(utm_medium && { utm_medium }),
    ...(utm_campaign && { utm_campaign }),
    ...(utm_content && { utm_content }),
    ...(utm_term && { utm_term }),
    ...(click_id && { click_id }),
    ...(click_id_type && { click_id_type }),
    ...(referrer && { referrer }),
    ...(referrer_internal && { referrer_internal }),
    ...(landing_slug && { landing_slug }),
    landed_at: page.now.toISOString(),
    device: detectDevice(page.userAgent),
    channel,
    is_paid,
  };
}

/**
 * The page brings its own source: a UTM, an ad click ID or a referrer from
 * another site. Internal navigation and direct loads do not.
 */
export function isSourceTouch(touch: Attribution): boolean {
  return (
    !!touch.click_id ||
    !!(
      touch.utm_source ||
      touch.utm_medium ||
      touch.utm_campaign ||
      touch.utm_content ||
      touch.utm_term
    ) ||
    (!!touch.referrer && !touch.referrer_internal)
  );
}

/**
 * How long a stored source keeps counting. Same window the CRM uses to credit
 * a WhatsApp campaign (30 days); a chat opened later counts as its own visit.
 */
export const ATTRIBUTION_TTL_DAYS = 30;

export interface StoredTouch {
  touch: Attribution;
  saved_at: string;
}

export interface ResolvedAttribution {
  attribution: Attribution;
  /** Touch to persist, or null to leave storage as it is. */
  toStore: StoredTouch | null;
}

/**
 * Last non-direct touch: a page with its own source wins and is stored; a page
 * without one inherits the stored source while it is fresh. With nothing
 * stored, the page is reported as is (direct, keeping the raw referrer).
 */
export function resolveAttribution(
  current: Attribution,
  stored: StoredTouch | null,
  now: Date,
): ResolvedAttribution {
  if (isSourceTouch(current)) {
    return {
      attribution: current,
      toStore: { touch: current, saved_at: now.toISOString() },
    };
  }
  const savedAt = stored ? Date.parse(stored.saved_at) : NaN;
  const fresh =
    !!stored &&
    !Number.isNaN(savedAt) &&
    now.getTime() - savedAt < ATTRIBUTION_TTL_DAYS * 86_400_000;
  if (fresh && stored) {
    return {
      attribution: { ...stored.touch, device: current.device, inherited: true },
      toStore: null,
    };
  }
  return { attribution: current, toStore: null };
}

/**
 * Capture attribution for this page load, remembering the source across the
 * visit. Call once on page load, before the handshake.
 *
 * The widget runs on the host page (Shadow DOM), so the stored touch lives in
 * the client's site storage and survives navigation between its pages.
 */
export function captureAttribution(): Attribution {
  const now = new Date();
  const current = readTouch({
    search: window.location.search,
    pathname: window.location.pathname,
    host: window.location.hostname,
    referrer: document.referrer,
    userAgent:
      typeof navigator === 'undefined' ? undefined : navigator.userAgent,
    now,
  });
  const { attribution, toStore } = resolveAttribution(
    current,
    readStoredAttribution(),
    now,
  );
  if (toStore) writeStoredAttribution(toStore);
  return attribution;
}
